import React, {useEffect, useState} from 'react';
import {
  FlexibleXYPlot,
  HorizontalGridLines,
  HorizontalBarSeries,
  HorizontalBarSeriesPoint,
  VerticalGridLines,
  XAxis,
  YAxis,
  Hint,
  LabelSeries,
} from 'react-vis';
import {MenuItem, Paper, Select, Typography, Grid} from '@material-ui/core';
import Tooltip from '@material-ui/core/Tooltip';
import './EnrollmentProcessingAccuracyChart.sass';
import {ProcessingAccuracyChartDetailsPayload} from 'Enrollment/models/ProcessingAccuracyChartDetailsPayload';
import {WithWidthProps} from '@material-ui/core/withWidth';
import {toKebabCase} from 'Shared/utilities/stringFormatHelpers';

type EnrollmentLifeCycleReportChartProps = {
  processingAccuracyChartDetails: ProcessingAccuracyChartDetailsPayload[];
} & WithWidthProps;

const EnrollmentProcessingAccuracyChart = (props: EnrollmentLifeCycleReportChartProps) => {
  const {processingAccuracyChartDetails} = props;
  const ProcessedColor = '#0033A0';
  const UnsuccessfulColor = '#F44336';
  const PendingColor = '#81D4FA';
  const ProcessedRecords = 'Successful';
  const UnsuccessfulRecords = 'Unsuccessful';
  const Pending = 'In Progress';
  const FixedDecimal = 2;
  const DefaultValue = '0.00';
  const xAxisLabelValue = 105;

  const [productTypesDropDown, setProductTypesDropDown] = useState<any[]>([]);
  const [selectedProductType, setSelectedProductType] = useState('');
  const [hintAxes, setHintAxes] = useState<HorizontalBarSeriesPoint | null>(null);
  const [hintText, setHintText] = useState<string | null>(null);
  const BarSeries = HorizontalBarSeries;

  //This to be done as we dont have barWidth attribute for Barseries available.
  const BarSeriesProps: any = {
    barWidth: 0.2,
  };

  useEffect(() => {
    const productTypeDropDownValues: string[] = processingAccuracyChartDetails.map(d => d.productType!);
    setProductTypesDropDown(productTypeDropDownValues.sort());
    setSelectedProductType('ALL');
  }, [processingAccuracyChartDetails]);

  const getRecordsData = (productType: string, statusType: string) => {
    const filteredProduct = productType
      ? processingAccuracyChartDetails.filter(files => files?.productType === productType && files)
      : null;
    const product = filteredProduct && filteredProduct.length > 0 ? filteredProduct[0] : null;
    const filteredRecords = processingAccuracyChartDetails
      .filter(files => files.productType === productType)
      .map(files => files.enrollmentRequestTypeDto)
      .map(records =>
        records!.map(record => ({
          y: `${record.requestType} - ${
            record.numberOfSuccessfulRecords! > 0 && record.totalRecords! > 0
              ? ((record.numberOfSuccessfulRecords! / record.totalRecords!) * 100).toFixed(FixedDecimal)
              : DefaultValue
          }%`,
          x:
            statusType === ProcessedRecords
              ? totalRecords(record.numberOfSuccessfulRecords!, product?.totalRecords!)
              : statusType === UnsuccessfulRecords
              ? totalRecords(record.numberOfUnsuccessfulRecords!, product?.totalRecords!)
              : totalRecords(record.numberOfPendingRecords!, product?.totalRecords!),
        }))
      );
    return filteredRecords && filteredRecords.length > 0 ? filteredRecords[0].reverse() : [];
  };

  const totalRecords = (recordCount: number, productTotalRecords: number) => {
    return productTotalRecords > 0 ? (recordCount / productTotalRecords) * 100 : 0;
  };

  const handleSeriesMouseOver = (e: HorizontalBarSeriesPoint, productType: string, statusType: string) => {
    const yAxisValue = e.y.toString();
    const requestType = yAxisValue ? yAxisValue.substring(0, yAxisValue.lastIndexOf('-')).trimRight() : null;
    const filteredProduct = productType
      ? processingAccuracyChartDetails.filter(files => files.productType === productType)
      : null;
    const product = filteredProduct && filteredProduct.length > 0 ? filteredProduct[0] : null;
    const filteredRecord = product?.enrollmentRequestTypeDto?.filter(record => record.requestType === requestType);
    const record = filteredRecord && filteredRecord.length > 0 ? filteredRecord[0] : null;
    if (record) {
      switch (statusType) {
        case ProcessedRecords:
          setHintText(
            `${requestType} - ${record.numberOfSuccessfulRecords!} - ${(
              (record.numberOfSuccessfulRecords! / product?.totalRecords! ?? 0) * 100
            ).toFixed(FixedDecimal)}%`
          );
          break;
        case UnsuccessfulRecords:
          setHintText(
            `${requestType} - ${record.numberOfUnsuccessfulRecords!} - ${(
              (record.numberOfUnsuccessfulRecords! / product?.totalRecords! ?? 0) * 100
            ).toFixed(FixedDecimal)}%`
          );
          break;
        case Pending:
          setHintText(
            `${requestType} - ${record.numberOfPendingRecords!} - ${(
              (record.numberOfPendingRecords! / product?.totalRecords! ?? 0) * 100
            ).toFixed(FixedDecimal)}%`
          );
          break;
        default:
          setHintText(null);
          break;
      }
    }
    setHintAxes(e);
  };

  const handleSeriesMouseOut = () => {
    setHintText(null);
  };

  const getLabelData = (productType: string) => {
    const filteredProduct = productType
      ? processingAccuracyChartDetails.filter(files => files.productType === productType)
      : null;
    const product = filteredProduct && filteredProduct.length > 0 ? filteredProduct[0] : null;
    const filteredRecords = product?.enrollmentRequestTypeDto?.map(record => ({
      y: `${record.requestType} - ${
        record.numberOfSuccessfulRecords! > 0 && record.totalRecords! > 0
          ? ((record.numberOfSuccessfulRecords! / record.totalRecords!) * 100).toFixed(FixedDecimal)
          : DefaultValue
      }%`,
      x: xAxisLabelValue,
      label: `${record.numberOfSuccessfulRecords! +
        record.numberOfUnsuccessfulRecords! +
        record.numberOfPendingRecords!}`,
    }));
    return filteredRecords && filteredRecords.length > 0 ? filteredRecords : [];
  };
  const getProcessingAccuracyRate = (productType: string) => {
    const filteredProduct = productType
      ? processingAccuracyChartDetails.filter(files => files.productType === productType)
      : null;
    const product = filteredProduct && filteredProduct.length > 0 ? filteredProduct[0] : null;
    if (product && product.totalRecords! > 0) {
      const successfulRecords =  product?.enrollmentRequestTypeDto?.map(record => record.numberOfSuccessfulRecords!);
      const unsuccessfulRecords =  product?.enrollmentRequestTypeDto?.map(record => record.numberOfUnsuccessfulRecords!);
      const sumOfSuccessfulRecords =  successfulRecords && successfulRecords.length > 0 ? successfulRecords.reduce((a, b) => a + b, 0) : 0;
      const sumOfUnsuccessfulRecords =  unsuccessfulRecords && unsuccessfulRecords.length > 0 ? unsuccessfulRecords.reduce((a, b) => a + b, 0) : 0;
      return `${ (100 * (sumOfSuccessfulRecords/(sumOfSuccessfulRecords + sumOfUnsuccessfulRecords))).toFixed(FixedDecimal) }%`;
      }
      return `${DefaultValue}%`;
  }
  const getStatusData = (productType: string, statusType: string) => {
    const filteredProduct = productType
      ? processingAccuracyChartDetails.filter(files => files.productType === productType)
      : null;
    const product = filteredProduct && filteredProduct.length > 0 ? filteredProduct[0] : null;

    if (product && product.totalRecords! > 0) {
      let recordsInStatusType: number[] | null | undefined;

      switch (statusType) {
        case ProcessedRecords:
          recordsInStatusType = product?.enrollmentRequestTypeDto?.map(record => record.numberOfSuccessfulRecords!);
          break;
        case UnsuccessfulRecords:
          recordsInStatusType = product?.enrollmentRequestTypeDto?.map(record => record.numberOfUnsuccessfulRecords!);
          break;
        case Pending:
          recordsInStatusType = product?.enrollmentRequestTypeDto?.map(record => record.numberOfPendingRecords!);
          break;
        default:
          recordsInStatusType = null;
          break;
      }

      const sumOfRecordsInStatusType =
        recordsInStatusType && recordsInStatusType.length > 0 ? recordsInStatusType.reduce((a, b) => a + b, 0) : 0;

      return `${
        sumOfRecordsInStatusType > 0 && product.totalRecords! > 0
          ? ((sumOfRecordsInStatusType / product.totalRecords!) * 100).toFixed(FixedDecimal)
          : DefaultValue
      }%`;
    }
    return `${DefaultValue}%`;
  };

  const convertToPercent = (value: number) => {
    return `${value}%`;
  };
  return (
    <Paper className="enrollment-processing-accuracy-chart">
      <div className="enrollment-processing-accuracy-chart__header">
        <Grid container spacing={2} justify="space-between">
          <Grid item>
            <h3
              id="enrollment-processing-accuracy-chart-title"
              className="enrollment-processing-accuracy-chart__header-title"
            >
              Processing Accuracy - {getProcessingAccuracyRate(selectedProductType)}
            </h3>
          </Grid>
        </Grid>
      </div>

      <div className="enrollment-processing-accuracy-chart__legend">
        <Grid container spacing={3}>
        <Grid item xs={12} md={4} lg={4} xl={4}>
            <div className="enrollment-processing-accuracy-chart__legend-item">
              <span className="legend-item__indicator inProgress" />
              <span
                id={`enrollment-processing-accuracy-chart-legend-${toKebabCase(Pending)}`}
                className="legend-item__title"
              >
                {Pending} - {getStatusData(selectedProductType, Pending)}
              </span>
            </div>
          </Grid>
          <Grid item xs={12} md={4} lg={4} xl={4}>
            <div className="enrollment-processing-accuracy-chart__legend-item">
              <span className="legend-item__indicator recordsProcessed" />
              <span
                id={`enrollment-processing-accuracy-chart-legend-${toKebabCase(ProcessedRecords)}`}
                className="legend-item__title"
              >
                {ProcessedRecords} - {getStatusData(selectedProductType, ProcessedRecords)}
              </span>
            </div>
          </Grid>
          <Grid item xs={12} md={4} lg={4} xl={4}>
            <div className="enrollment-processing-accuracy-chart__legend-item">
              <span className="legend-item__indicator unsuccessfulRecords" />
              <span
                id={`enrollment-processing-accuracy-chart-legend-${toKebabCase(UnsuccessfulRecords)}`}
                className="legend-item__title"
              >
                {UnsuccessfulRecords} - {getStatusData(selectedProductType, UnsuccessfulRecords)}
              </span>
            </div>
          </Grid>
          
        </Grid>
      </div>

      <div className="enrollment-processing-accuracy-chart__container">
        <h4>
          <span className="enrollment-processing-accuracy-chart__container-statistics-title">
            Request Type - Success %
          </span>
          <span className="enrollment-processing-accuracy-chart__container-statistics-title-total">Total</span>
        </h4>
        <FlexibleXYPlot yType="ordinal" height={300} stackBy="x" margin={{left: 205, right: 25}}>
          <VerticalGridLines />
          <HorizontalGridLines />
          <XAxis
            tickFormat={(v: number) => convertToPercent(v)}
            tickValues={[10, 20, 30, 40, 50, 60, 70, 80, 90, 100]}
          />
          <YAxis />
          <BarSeries
            {...BarSeriesProps}
            data={getRecordsData(selectedProductType, ProcessedRecords)}
            color={ProcessedColor}
            onValueMouseOver={e => handleSeriesMouseOver(e, selectedProductType, ProcessedRecords)}
            onValueMouseOut={handleSeriesMouseOut}
          />
          <BarSeries
            {...BarSeriesProps}
            data={getRecordsData(selectedProductType, UnsuccessfulRecords)}
            color={UnsuccessfulColor}
            onValueMouseOver={e => handleSeriesMouseOver(e, selectedProductType, UnsuccessfulRecords)}
            onValueMouseOut={handleSeriesMouseOut}
          />
          <BarSeries
            {...BarSeriesProps}
            data={getRecordsData(selectedProductType, Pending)}
            color={PendingColor}
            onValueMouseOver={e => handleSeriesMouseOver(e, selectedProductType, Pending)}
            onValueMouseOut={handleSeriesMouseOut}
          />
          <LabelSeries data={getLabelData(selectedProductType)} />
          {hintAxes && hintText && (
            <Hint
              xType="literal"
              yType="ordinal"
              align={{vertical: 'top', horizontal: 'rightEdge'}}
              value={{x: hintAxes.x, y: hintAxes.y}}
            >
              <Tooltip title="Hint" className="enrollment-processing-accuracy-chart__container-tooltip">
                <Typography> {hintText} </Typography>
              </Tooltip>
            </Hint>
          )}
        </FlexibleXYPlot>
      </div>
    </Paper>
  );
};
export default EnrollmentProcessingAccuracyChart;
